<script lang="ts" setup>
	import { useTheme } from "~/composables/useTheme";

	// "wordmark" is the full LEADX lockup; "mark" crops the same drawing to the
	// gradient X so the collapsed sidebar keeps the brand without the letters.
	// The ® reads as a speck below roughly 24px tall, so app chrome turns it off
	// while the full-size marketing and auth lockups keep it.
	const props = withDefaults(
		defineProps<{
			variant?: "wordmark" | "mark";
			registered?: boolean;
		}>(),
		{ variant: "wordmark", registered: true }
	);

	const { theme } = useTheme();

	const isMark = computed(() => props.variant === "mark");

	// Tight ink bounds of the artwork, measured in the drawing's own units:
	// the letters start at 0, the X runs 122.28–156.5, and the ® trails to 160.4.
	const box = computed(() => {
		if (isMark.value) return { x: 122.28, width: props.registered ? 38.2 : 34.3 };
		return { x: 0, width: props.registered ? 161.01 : 157 };
	});

	const viewBox = computed(() => `${box.value.x} 0 ${box.value.width} 22.73`);
	// Gradient ids are global to the document, so two variants on one page must
	// not share them.
	const gid = computed(() => `leadx-logo-${props.variant}`);
</script>

<template>
	<svg class="logo" :class="variant" xmlns="http://www.w3.org/2000/svg" :width="box.width" height="22.73" xmlns:xlink="http://www.w3.org/1999/xlink" :viewBox="viewBox" role="img" aria-label="LeadX">
		<defs>
			<linearGradient :id="`${gid}-1`" x1="122.91" y1="21.66" x2="156.97" y2="1.43" gradientTransform="translate(0 .02) rotate(-.02)" gradientUnits="userSpaceOnUse">
				<stop offset="0" stop-color="#7f1416" />
				<stop offset=".08" stop-color="#8a1717" />
				<stop offset=".23" stop-color="#a81f1b" />
				<stop offset=".43" stop-color="#d92d21" />
				<stop offset=".51" stop-color="#ee3324" />
				<stop offset=".79" stop-color="#f15c3d" />
				<stop offset="1" stop-color="#f47e53" />
			</linearGradient>
			<linearGradient :id="`${gid}-2`" x1="131.38" y1="7.47" x2="131.38" y2="-1.5" gradientTransform="translate(0 .02) rotate(-.02)" gradientUnits="userSpaceOnUse">
				<stop offset="0" stop-color="#7f1416" />
				<stop offset="1" stop-color="#f47e53" />
			</linearGradient>
			<linearGradient :id="`${gid}-3`" x1="147.5" y1="14.85" x2="147.5" y2="24.71" :xlink:href="`#${gid}-2`" />
		</defs>
		<g v-if="!isMark">
			<path :fill="theme === 'light' ? '#25292b' : '#F5F5F5'" d="M5.83,1.2v15.79s17.48,0,17.48,0v5.08S0,22.06,0,22.06V1.2s5.82,0,5.82,0Z" />
			<path
				:fill="theme === 'light' ? '#25292b' : '#F5F5F5'"
				d="M52.23,1.18v5.08s-18.45,0-18.45,0v2.77s15.99,0,15.99,0v4.99s-15.99,0-15.99,0v2.95s18.45,0,18.45,0v5.08s-24.28,0-24.28,0V1.19s24.27,0,24.27,0Z"
			/>
			<path
				:fill="theme === 'light' ? '#25292b' : '#F5F5F5'"
				d="M73.93,1.18l12.52,20.86h-6.29s-2.65-4.45-2.65-4.45h-12.63s-2.68,4.45-2.68,4.45h-6.29S68.43,1.18,68.43,1.18h5.5ZM67.77,12.75h6.84s-3.41-5.69-3.41-5.69l-3.42,5.69Z"
			/>
			<path
				:fill="theme === 'light' ? '#25292b' : '#F5F5F5'"
				d="M110.76,1.17c1.82,0,3.43.61,4.81,1.83,1.42,1.22,2.11,2.68,2.08,4.38v8.46c0,1.7-.69,3.16-2.09,4.38-1.38,1.21-2.98,1.82-4.79,1.82h-20.65s0-20.86,0-20.86h20.65ZM95.94,16.96h13.72c.6-.04,1.12-.24,1.55-.62.43-.42.64-.91.63-1.48v-6.52c0-.59-.22-1.09-.64-1.48-.42-.42-.93-.62-1.53-.61h-13.72s0,10.72,0,10.72Z"
			/>
		</g>
		<path
			:fill="`url(#${gid}-1)`"
			d="M144.34,21.99l-5.25-5.31-3.36,3.4c-1.21,1.22-2.82,1.91-4.49,1.91h-8.96s11.02-11.09,11.02-11.09l-9.73-9.61h11.63s3.89,4.03,3.89,4.03l2.02-2.1c1.19-1.24,2.8-1.93,4.47-1.93h9.02s-9.72,9.61-9.72,9.61l11.02,11.08h-11.57Z"
		/>
		<path :fill="`url(#${gid}-2)`" d="M139.13,5.33l-3.89-4.03h-11.63c4.44,4.07,10.83,8.41,15.52,4.03Z" />
		<path :fill="`url(#${gid}-3)`" d="M155.91,21.99c-7.09-6.99-13.68-8.23-16.81-5.3l5.25,5.31h11.57Z" />
		<path
			v-if="registered"
			fill="#ef3e27"
			d="M158.67,3.62c-.24,0-.46-.05-.68-.14-.21-.09-.39-.22-.55-.38-.16-.16-.28-.35-.37-.58-.09-.22-.13-.46-.13-.72s.04-.5.13-.72c.09-.22.21-.42.37-.58.16-.16.34-.29.55-.38.21-.09.44-.14.67-.14s.46.05.68.14c.21.09.39.22.55.38.15.16.28.35.37.58s.14.46.14.72-.05.5-.14.72c-.09.22-.21.42-.37.58-.15.16-.34.29-.55.38-.21.09-.44.14-.67.14ZM158.67,3.21c.18,0,.35-.03.5-.1.15-.06.29-.15.4-.27.11-.11.2-.25.27-.41.07-.16.1-.34.1-.54v-.2c0-.2-.03-.38-.1-.54-.07-.16-.16-.3-.27-.41s-.25-.2-.4-.26c-.15-.06-.32-.09-.5-.09s-.35.03-.5.1c-.15.06-.29.15-.4.27-.11.11-.2.25-.27.41-.07.16-.1.34-.1.54v.2c0,.2.03.38.1.54.07.16.16.3.27.41.11.11.25.2.4.26.15.06.32.09.5.09ZM158.38,2.72h-.38s0-1.85,0-1.85h.8c.18,0,.31.05.41.15s.15.24.15.41c0,.26-.12.43-.35.52l.43.76h-.43s-.37-.7-.37-.7h-.27s0,.7,0,.7ZM158.72,1.73c.16,0,.25-.08.25-.24v-.07c0-.16-.08-.24-.25-.24h-.34s0,.55,0,.55h.34Z"
		/>
	</svg>
</template>

<style scoped lang="scss">
	/* Sized by whoever places it; the SVG keeps its intrinsic 22.73px height otherwise. */
</style>
